import { useEffect, useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import type { ColDef, ColGroupDef } from 'ag-grid-community'
import { startCase } from 'lodash-es'

import { useAppSelector } from 'app/hooks'
import {
  IGetQRATableQueryParams,
  useAddQRATableExportJobMutation,
  useGetQRATableQuery
} from 'app/services/api/qra/datatable'
import {
  IGetQRAGraphsQueryParams,
  IQRAGraphCsvExportParams
} from 'app/services/api/qra/graphs'
import { useGetQRASavedStateQuery } from 'app/services/api/qra/savedstates'
import { selectSystemScopeIdContainer } from 'app/layout/Navbar/Filters/globalFilterSelectors'
import { useToggleRiskDensity } from 'common/hooks/useToggleRiskDensity'
import { getLineIdFromQRAViewBasePath } from 'common/utils/navigation/qraNavUtils'

import { useQraGraphOverride } from '_/src/features/RiskManagement/Components/Graphs/useQraGraphOverride'
import { IQRAGraphBools } from 'features/RiskManagement/Components/Graphs'
import GraphsView from 'features/RiskManagement/Components/Graphs/Layout/LineView'
import {
  selectRiskUnit,
  selectSavedFilter
} from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'
import {
  QRATableLineViewMode,
  type IGroupColumnsSelection
} from 'features/RiskManagement/Components/Tables'
import TableView from 'features/RiskManagement/Components/Tables/Layout/LineView'
import { convertTableAPIDataToQRATableComponentProps } from 'features/RiskManagement/Components/Tables/utils/data'
import { getGraphUnits } from 'features/RiskManagement/GraphUnits'

import { ISavedStateGraphsInitProps, ISavedStateTableInitProps } from './types'

// #region Line Graphs View
const LineGraphsInit = ({
  qraBasePath,
  qraViewBasePath,
  stateId,
  assessmentId
}: ISavedStateGraphsInitProps) => {
  const activeFilterProfileIdContainer = useAppSelector(
    selectSystemScopeIdContainer
  )

  const localFilter = useAppSelector(selectSavedFilter)
  const riskUnit = useAppSelector(selectRiskUnit)

  const { currentData: currentState } = useGetQRASavedStateQuery(
    stateId ?? skipToken
  )

  const continuousRouteId = getLineIdFromQRAViewBasePath(qraViewBasePath)

  const graphSelections: IQRAGraphBools = {
    riskbyconsequence: true,
    riskbyline: true,
    riskbylinearreference: true,
    riskforecast: true
  }

  const shouldSkipGraphs =
    activeFilterProfileIdContainer.id == null || currentState == null

  const qraGraphsAPIParams: IGetQRAGraphsQueryParams = {
    systemScopeId: activeFilterProfileIdContainer.id?.toString() ?? '',
    localFilter,
    riskUnit,
    savedStateId1: stateId,
    assessmentId,
    continuousRouteId,
    graphSelections
  }

  const {
    charts,
    riskByPipelineLengthGraphData,
    riskBySystemGraphData,
    isFetching,
    graphLoadingByType
  } = useQraGraphOverride({
    graphsQueryParams: qraGraphsAPIParams,
    skip: shouldSkipGraphs,
    refetchOn: activeFilterProfileIdContainer
  })

  const graphCsvExportParams: IQRAGraphCsvExportParams = {
    systemScopeId: activeFilterProfileIdContainer.id?.toString() ?? '',
    continuousRouteId,
    savedStateId: stateId,
    localFilter,
    riskUnit
  }

  return (
    <GraphsView
      qraBasePath={qraBasePath}
      qraViewBasePath={qraViewBasePath}
      showLoading={isFetching}
      charts={charts}
      riskByPipelineLengthGraphData={riskByPipelineLengthGraphData}
      riskBySystemGraphData={riskBySystemGraphData}
      graphLoadingByType={graphLoadingByType}
      csvExportParams={graphCsvExportParams}
    />
  )
}
// #endregion

// #region Line Table View
const LineTableInit = ({
  qraViewBasePath,
  stateId,
  assessmentId
}: ISavedStateTableInitProps) => {
  useToggleRiskDensity()

  const activeFilterProfileIdContainer = useAppSelector(
    selectSystemScopeIdContainer
  )

  const localFilter = useAppSelector(selectSavedFilter)
  const riskUnit = useAppSelector(selectRiskUnit)

  const { currentData: currentState } = useGetQRASavedStateQuery(
    stateId ?? skipToken
  )

  const continuousRouteId = getLineIdFromQRAViewBasePath(qraViewBasePath)

  const [lineViewMode, setLineViewMode] = useState<QRATableLineViewMode>(
    QRATableLineViewMode.Output
  )

  const [groupColumns, setGroupColumns] = useState<IGroupColumnsSelection[]>([
    { id: 0, name: 'Threat', groupTypeColumns: undefined }
  ])

  const isOutputTableView = lineViewMode === QRATableLineViewMode.Output

  const qraTableQueryParams: IGetQRATableQueryParams = {
    systemScopeId: activeFilterProfileIdContainer.id?.toString() ?? '',
    localFilter,
    riskUnit,
    tableTypeSelections:
      groupColumns.length > 0
        ? groupColumns.map((group) =>
            isOutputTableView ? startCase(group.name) : group.id.toString()
          )
        : [null],
    lineViewMode: isOutputTableView ? 'Output' : 'Input',
    savedStateId1: stateId,
    assessmentId: assessmentId,
    continuousRouteId
  }

  const {
    data: tableData,
    isFetching,
    isUninitialized,
    refetch
  } = useGetQRATableQuery(qraTableQueryParams, {
    skip: activeFilterProfileIdContainer.id == null || currentState == null
  })

  const [addQRATableExportJob] = useAddQRATableExportJobMutation()

  const [colDefs, rowData] = useMemo(
    () =>
      convertTableAPIDataToQRATableComponentProps(
        tableData,
        groupColumns,
        assessmentId != null
      ),
    [assessmentId, groupColumns, tableData]
  )

  const pinnedDefs = useMemo(
    () =>
      colDefs.map((colDef: ColDef | ColGroupDef) => {
        return {
          ...colDef,
          children: (colDef as ColGroupDef).children?.map((child) => ({
            ...child,
            pinned: (colDef as ColDef).pinned
          }))
        }
      }),
    [colDefs]
  )

  useEffect(() => {
    if (!isUninitialized) {
      refetch()
    }
  }, [activeFilterProfileIdContainer, isUninitialized, refetch])

  const unitData = getGraphUnits(tableData)

  return (
    <TableView
      showLoading={isFetching}
      colDefs={pinnedDefs}
      rowData={rowData}
      units={unitData}
      s1Label={tableData?.dataset1Rows.name}
      groupColumns={groupColumns}
      setGroupColumns={setGroupColumns}
      lineViewMode={lineViewMode}
      setLineViewMode={setLineViewMode}
      mutationTrigger={addQRATableExportJob}
      exportParams={qraTableQueryParams}
      isCompareView={assessmentId != null}
    />
  )
}
// #endregion

export { LineGraphsInit, LineTableInit }
